import React, { useState } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { DataProvider } from "./contexts/DataContext";
import SignupForm from "./components/Auth/SignupForm";
import LoginForm from "./components/Auth/LoginForm";
import AuthCallback from "./components/Auth/AuthCallback";
import ResourcesHub from "./components/Resources/ResourcesHub";
import CalculatorsPage from "./components/Calculators/CalculatorsPage";
import Header from "./components/Header";
import Sidebar from "./components/Sidebar";
import Dashboard from "./components/Dashboard";
import ProfilePage from "./components/Profile/ProfilePage";
import { Loader2 } from "lucide-react";

const AuthenticatedApp = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isChatMinimized, setIsChatMinimized] = useState(true);

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const closeSidebar = () => {
    setIsSidebarOpen(false);
  };

  const openChat = () => {
    setIsChatMinimized(false);
  };

  const toggleChat = () => {
    setIsChatMinimized(!isChatMinimized);
  };

  return (
    <DataProvider>
      <div className="flex h-screen bg-slate-100">
        <Sidebar isOpen={isSidebarOpen} onClose={closeSidebar} onOpenChat={openChat} />
        <div className="flex-1 flex flex-col overflow-hidden">
          <Header onMenuToggle={toggleSidebar} isMenuOpen={isSidebarOpen} />
          <main className="flex-1 overflow-y-auto">
            <Routes>
              <Route path="/" element={<Dashboard chatMinimized={isChatMinimized} onToggleChat={toggleChat} />} />
              <Route path="/dashboard" element={<Dashboard chatMinimized={isChatMinimized} onToggleChat={toggleChat} />} />
              <Route path="/profile" element={<ProfilePage />} />
              <Route path="/resources" element={<ResourcesHub />} />
              <Route path="/calculators" element={<CalculatorsPage />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </main>
        </div>
      </div>
    </DataProvider>
  );
};

const UnauthenticatedApp = () => {
  const [showSignup, setShowSignup] = useState(false);
  const { login } = useAuth();

  const handleLoginSuccess = (userData) => {
    login(userData);
  };

  const handleSignupSuccess = (userData) => {
    login(userData);
  };

  const switchToSignup = () => setShowSignup(true);
  const switchToLogin = () => setShowSignup(false);

  return (
    <Routes>
      <Route 
        path="*" 
        element={
          showSignup ? (
            <SignupForm 
              onSignupSuccess={handleSignupSuccess}
              onSwitchToLogin={switchToLogin}
            />
          ) : (
            <LoginForm 
              onLoginSuccess={handleLoginSuccess}
              onSwitchToSignup={switchToSignup}
            />
          )
        } 
      />
    </Routes>
  );
};

const AppContent = () => {
  const { loading } = useAuth();
  const location = useLocation();

  // CRITICAL: Detect session_id in URL hash synchronously during render
  // This MUST happen BEFORE ProtectedRoute or AuthContext checks run
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-slate-900 flex items-center justify-center">
        <div className="flex items-center gap-3 text-white">
          <Loader2 className="w-8 h-8 animate-spin" />
          <span className="text-lg font-medium">Loading MilConnect AI...</span>
        </div>
      </div>
    );
  }

  return <AppRoutes />;
};

const AppRoutes = () => {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? <AuthenticatedApp /> : <UnauthenticatedApp />;
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
